import { ICommandReceiver } from "./ICommandReceiver";
import { ISimpleCommand } from "./ISimpleCommand";

import { fatal } from "../logging/fatal";
import { warn } from "../logging/warn";

/**
 * <p>GoodgameCommands is a simple and small Command-Framework based on the command pattern.</p>
 *
 * @example register a new command and execute it
 *
 * <listing version="3.0">
 public class GoodgameCommandsExample
 {
      public let cmdReceiver:ExampleCommandReceiver = new ExampleCommandReceiver();

     public function GoodgameCommandsExample()
     {
        CommandController.instance.registerCommand(ExampleCommand,cmdReceiver);
        CommandController.instance.executeCommand(ExampleCommand);
     }
 }
 * </listing>
 *
 * @author dkoehler
 */
export class CommandController {
    private static _instance: CommandController;

    /** instance of this singleton. */
    public static get instance(): CommandController {
        if (!CommandController._instance) {
            CommandController._instance = new CommandController();
        }
        return CommandController._instance;
    }

    /** map with all commands. */
    private commandMap: Map<any, any> = new Map<any, any>();

    constructor() {
        if (CommandController._instance) {
            throw new Error("this is a singleton. cannot instanciate more than once");
        }
    }

    /**
     * register a new command with an optional receiver. if command is already exisitng, it will be overwritten
     *
     * @param name of command that is connected to command-class
     * @param commandClass concrete command class
     * @param commandReceiver receiver for this command
     * @param singleExecution flag, if command should be executed only once, independent of call-amount
     *
     */
    public registerCommand(commandName: string, commandClass: any, commandReceiver: ICommandReceiver = null, singleExecution: boolean = false): void {
        let commandClassInstance: ISimpleCommand = this.commandMap.get(commandName);

        if (commandClassInstance) {
            commandClassInstance.removeAllReceiver();
        }
        commandClassInstance = new commandClass();
        this.commandMap.set(commandName, commandClassInstance);

        commandClassInstance.singleExecution = singleExecution;

        if (commandReceiver) {
            commandClassInstance.addReceiver(commandReceiver);
        }
    }

    /**
     * add a receiver to a specific command
     *
     * @param commandReceiver receiver to add
     * @param commandName command-name that receiver will be bind on
     *
     */
    public addReceiverToCommand(commandReceiver: ICommandReceiver, commandName: string): void {
        let commandClassInstance: ISimpleCommand = this.commandMap.get(commandName);

        if (commandClassInstance) {
            commandClassInstance.addReceiver(commandReceiver);
        }
        else {
            fatal("cannot add commandreceiver to non existing command");
        }
    }

    /**
     *
     * remove a receiver from a specific command
     *
     * @param commandReceiver receiver to remove
     * @param commandName command-name that receiver will be bind off
     *
     */
    public removeReceiverFromCommand(commandReceiver: ICommandReceiver, commandName: string): void {
        let commandClassInstance: ISimpleCommand = this.commandMap.get(commandName);

        if (commandClassInstance) {
            commandClassInstance.removeReceiver(commandReceiver);
        }
        else {
            fatal("cannot remove commandreceiver from non existing command");
        }
    }

    /**
     * execute command and after that call command receiver action
     *
     * @param commandName registered command that should be executed
     * @param commandVars optional parameters for command
     */
    public executeCommand(commandName: string, commandVars: Object = null): void {
        let cmdClassInstance: ISimpleCommand;

        if (this.commandMap.get(commandName)) {
            cmdClassInstance = this.commandMap.get(commandName);

            if (cmdClassInstance.executed && cmdClassInstance.singleExecution) {
                let errorString: string = commandName + " was already executed";
                warn(errorString);
            }
            else {
                cmdClassInstance.execute(commandVars);

                let length: number = cmdClassInstance.commandReceiver.length;

                for (let i: number = 0; i < length; i++) {
                    cmdClassInstance.commandReceiver[i].handleCommand(commandName, commandVars);
                }
                cmdClassInstance.executed = true;
            }
        }
        else {
            warn(commandName + " has no instance in commandmap");
        }
    }

    /**
     * get reference to execute-function from specifc command
     *
     * @param commandName name of command where to get the execute function
     * @return refernce to execute-function or null if command is not registered
     *
     */
    public getExecFunctionFromCommand(commandName: string): Function {
        let cmdClassInstance: ISimpleCommand;

        for (let commandString of Array.from(this.commandMap.keys())) {
            if (commandName == commandString) {
                if (this.commandMap.get(commandString)) {
                    cmdClassInstance = this.commandMap.get(commandString);
                    return cmdClassInstance.execute;
                }
                else {
                    fatal(commandName + "has no instance in commandmap");
                    return null;
                }
            }
        }
        fatal(commandName + "unknown error while retrieving execute function from command");
        return null;
    }
}
